// Import of creatApp from Vue 3 bundler and axios for API requests.
import { createApp } from 'vue/dist/vue.esm-bundler';
import AgendaItem from './components/AgendaItem.vue';
import Skeleton from './components/Skeleton.vue';
import mixin from '../mixin.js';
import axios from 'axios';

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

import Datepicker from 'vanillajs-datepicker/Datepicker';
import nl from 'vanillajs-datepicker/locales/nl';
Object.assign(Datepicker.locales, nl);

import { debounce, setCookie, getCookie, deleteCookie } from '../functions';

const agendaApp = {
    components: { AgendaItem, Skeleton },
    mixins: [mixin],
    // The data object returns data-attribute values instantiated in HTML and general properties for loading state, 
    // the data object with facets and resulting items, the view toggler for results, the current 'page' and textual search input binding.
    data() {
        return {
            dataApi: '',
            dataLimit: 10, // Default limit in BE
            dataSortKey: '',
            dataSortDirection: '',
            dataAmountsSentence: '',
            dataTodayLabel: 'vandaag',
            dataWeekendLabel: 'dit weekend',
            dataWeekLabel: 'deze week',

            loading: true,
            data: null,
            blockView: true,
            page: 1,

            searchText: '',
            searchFrom: '',
            searchTo: '',
            searchDateLabel: '',
            calendar: null,
            calendarChangeHandler: null,

            isInputFocused: false,
            selectedIndex: -1,
            searchFields: [{
                Key: 'title',
                Boost: 1.2,
                DoSuggestions: true
            },
            {
                Key: 'subtitle',
                Boost: 1,
                DoSuggestions: true
            }]
        }
    },
    watch: {
        blockView(newView) {
            if (!newView) {
                setCookie('listBlockView', 'false', 365);
            } else {
                deleteCookie('listBlockView');
            }
        }
    },
    // The computed object holds checks for facets set, filters chosen and filters active.
    // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
    computed: {
        facetsSet() {
            return this.data && this.data.facets && this.data.facets.length;
        },
        filtersChosen() {
            return this.facetsSet && this.data.facets.some(facet => facet.values.length);
        },
        filtersActive() {
            return this.facetsSet && (this.data.facets.some(facet => facet.selected) || this.searchText || (this.searchFrom && this.searchTo));
        },
        orderItems() {
            if (!this.dataSortKey && !this.dataSortDirection) return [];
            return [
                {
                    Key: this.dataSortKey,
                    Direction: this.dataSortDirection,
                }
            ]
        },
        filterItems() {
            if (!this.filtersChosen && !this.searchFrom && !this.searchTo) return [];
            let tempFilterArray = this.data.facets.filter(facet => facet.values.length).map(facet => {
                return {
                    Key: facet.alias,
                    Values: facet.values,
                    Operator: 'In',
                }
            });
            if (this.searchFrom && this.searchTo) {
                tempFilterArray.push({
                    Key: 'startDate',
                    Values: [this.searchFrom],
                    Operator: 'Gt',
                },
                    {
                        Key: 'startDate',
                        Values: [this.searchTo],
                        Operator: 'Lt',
                    })
            }
            return tempFilterArray;
        },
        queryModel() {
            return {
                Limit: this.dataLimit,
                Offset: (this.page - 1) * this.dataLimit,
                OrderItems: this.orderItems,
                FilterItems: this.filterItems,
                Text: this.searchText,
                SearchFields: this.searchFields,
                SuggestionLimit: 10
            }
        },
        amountsSentence() {
            return this.dataAmountsSentence.replace('{{queryCount}}', this.data.queryCount).replace('{{totalCount}}', this.data.totalCount);
        },
        customDateButtons() {
            let today = {
                label: this.dataTodayLabel,
                from: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
                to: new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
            }

            let weekendFrom = new Date();
            let day = weekendFrom.getDay();
            if (!day) weekendFrom.setHours(-24 * 2);
            if (day === 6) weekendFrom.setHours(-24);
            if (day && day < 5) weekendFrom.setDate(weekendFrom.getDate() + (12 - weekendFrom.getDay()) % 7);

            let weekendTo = new Date(weekendFrom);
            weekendTo.setHours(24 * 2);

            let weekend = {
                label: this.dataWeekendLabel,
                from: new Date(weekendFrom.setHours(0, 0, 0, 0)).toISOString(),
                to: new Date(weekendTo.setHours(23, 59, 59, 999)).toISOString()
            }

            let weekFrom = new Date();
            day = weekFrom.getDay();
            if (!day) weekFrom.setHours(-24 * 6)
            if (day && day > 1) weekFrom.setHours(-24 * (day - 1));

            let weekTo = new Date(weekFrom);
            weekTo.setHours(24 * 6);

            let week = {
                label: this.dataWeekLabel,
                from: new Date(weekFrom.setHours(0, 0, 0, 0)).toISOString(),
                to: new Date(weekTo.setHours(23, 59, 59, 999)).toISOString()
            }

            return [today, weekend, week];
        },
        monthButtons() {
            let dateFacet = this.data.facets.find(facet => facet.inputType === 'buttons');
            if (!dateFacet && dateFacet.length) return [];

            let dateOptions = dateFacet.options.map(option => {
                option.valueAlias = option.valueAlias.replace(' ', 'T');
                return option;
            });

            if (!dateOptions.length) return [];

            let firstDate = new Date(dateOptions[0].valueAlias).setDate(1);
            let lastDate = new Date(dateOptions[dateOptions.length - 1].valueAlias);
            lastDate.setMonth(lastDate.getMonth() + 1);

            let monthButtons = [];

            let date = new Date(firstDate);
            do {
                if (new Date(date.getFullYear(), date.getMonth() + 1, 0) > new Date()) {
                    monthButtons.push({
                        year: date.getFullYear(),
                        month: date.getMonth(),
                        label: date.toLocaleDateString('nl-NL', { month: 'short' }).replace('.', ''),
                        from: new Date(date.getFullYear(), date.getMonth(), 1).toISOString(),
                        to: new Date(date.getFullYear(), date.getMonth() + 1, 1).toISOString(),
                        disabled: !dateOptions.some(option =>
                            new Date(option.valueAlias).getFullYear() === date.getFullYear() &&
                            new Date(option.valueAlias).getMonth() === date.getMonth())
                    })
                }
                date.setMonth(date.getMonth() + 1);
            }
            while ((date.getFullYear() !== lastDate.getFullYear()) || (date.getMonth() !== lastDate.getMonth()))

            return monthButtons;
        }
    },
    methods: {
        // The fetchData method fetches and handles API data, mainly facets and resulting items. 
        // The parameters ensure the method can be called in different ways.
        fetchData(initial = false, filterParams = null, pageParam = 0, stateUpdate = true) {
            if (initial) this.page = 1;
            let tempQueryModel = { ...this.queryModel };
            axios
                .post(this.dataApi, this.queryModel)
                .then(response => {
                    if (response.status === 200) {
                        // Check if the data object should be overwritten.
                        if (initial) {
                            this.data = response.data;

                            if (this.data.suggestedSearchTerms) {
                                const seen = new Set();
                                this.data.suggestedSearchTerms =
                                    this.data.suggestedSearchTerms.reduce(
                                        (acc, term) => {
                                            const matchingItem =
                                                this.data.items.find(
                                                    (item) =>
                                                        item.title ===
                                                            term.text ||
                                                        item.subtitle ===
                                                            term.text
                                                );
                                            if (!matchingItem)
                                                return acc;
                                            if (
                                                seen.has(
                                                    matchingItem.agendaItemUrl
                                                )
                                            )
                                                return acc;
                                            seen.add(
                                                matchingItem.agendaItemUrl
                                            );
                                            acc.push({
                                                ...term,
                                                agendaItemUrl:
                                                    matchingItem.agendaItemUrl,
                                                startDate:
                                                    matchingItem.startDate,
                                                title: matchingItem.title,
                                                subtitle:
                                                    matchingItem.subtitle,
                                                imageSets:
                                                    matchingItem.imageSets,
                                            });
                                            return acc;
                                        },
                                        []
                                    );
                            }

                            // Add values array to facet items, for easier access of set filters.
                            this.data.facets = response.data.facets.map(facet => {
                                return {
                                    ...facet,
                                    values: [],
                                }
                            }).sort((a, b) => a.sortOrder - b.sortOrder);

                            // Set selected values on facets.
                            if (tempQueryModel.FilterItems && tempQueryModel.FilterItems.length) {
                                tempQueryModel.FilterItems.forEach(filter => {
                                    if (filter.Operator !== 'In') return;
                                    let matchingFacet = this.data.facets.find(facet => facet.alias === filter.Key);
                                    matchingFacet.selected = true;
                                    matchingFacet.values = filter.Values;
                                    filter.Values.forEach(value => {
                                        matchingFacet.options.find(option => option.valueAlias === value).selected = true;
                                    })
                                })
                            }

                            // Check if preset filters were given in the URL or the initial load should yield more than 1 data chunk.
                            if (filterParams) {
                                return { action: 'PREFILTER', payload: filterParams };
                            }
                            else if (pageParam && pageParam > 1) {
                                return { action: 'LOADMORE', payload: pageParam };
                            }
                        }
                        // If no overwrite, add items to the existing items array. This is for lazy loading purposes.
                        else {
                            response.data.items.forEach(item => this.data.items.push(item));
                        }

                        // Push state to browser URL.
                        if (stateUpdate) this.updateUrl();
                    }
                })
                .then(resolve => {
                    // Handle preset filters and/or loading more data chunks in initial page load.
                    if (resolve && resolve.action) {
                        switch (resolve.action) {
                            case 'PREFILTER':
                                this.data.facets.forEach(facet => {
                                    if (resolve.payload.has(facet.title.toLowerCase())) {
                                        let values = resolve.payload.get(facet.title.toLowerCase()).split(',');
                                        values.forEach(value => {
                                            if (facet.options.find(option => option.text.toLowerCase() === value)) {
                                                facet.values.push(facet.options.find(option => option.text.toLowerCase() === value).valueAlias)
                                            }
                                        })
                                    }
                                    if (facet.alias === 'startDate' && resolve.payload.has('date')) {
                                        let dateValue = resolve.payload.get('date');
                                        if (this.customDateButtons.some(button => button.label === dateValue)) {
                                            let button = this.customDateButtons.find(button => button.label === dateValue);
                                            this.searchFrom = button.from;
                                            this.searchTo = button.to;
                                            this.searchDateLabel = button.label;
                                        }
                                        else if (this.monthButtons.some(button => button.label + button.year === dateValue)) {
                                            let button = this.monthButtons.find(button => button.label + button.year === dateValue);
                                            this.searchFrom = button.from;
                                            this.searchTo = button.to;
                                            this.searchDateLabel = button.label + button.year;
                                        }

                                        else {
                                            let dateArray = dateValue.split('-');
                                            let formattedDate = new Date(dateArray[2], dateArray[1] - 1, dateArray[0]);

                                            if (formattedDate !== 'Invalid Date' && !isNaN(formattedDate)) {
                                                if (formattedDate.toISOString() === new Date(new Date().setHours(0, 0, 0)).toISOString) {
                                                    let button = this.customDateButtons.find(button => button.label === 'vandaag');
                                                    this.searchFrom = button.from;
                                                    this.searchTo = button.to;
                                                    this.searchDateLabel = button.label;
                                                }
                                                else {
                                                    this.searchFrom = new Date(formattedDate.setHours(0, 0, 0, 0)).toISOString();
                                                    this.searchTo = new Date(formattedDate.setHours(23, 59, 59, 999)).toISOString();
                                                    this.searchDateLabel = dateValue;
                                                }
                                            }
                                        }
                                    }
                                });
                                this.fetchData(true, null, pageParam, false);
                                break;
                            case 'LOADMORE':
                                this.page++;
                                let maxPage = resolve.payload;
                                if (this.data.totalCount < resolve.payload * this.dataLimit) maxPage = Math.ceil(this.data.totalCount / this.dataLimit);
                                for (this.page; this.page <= maxPage; this.page++) {
                                    this.fetchData(false, null, 0, false);
                                }
                                this.page--;
                                break;
                            default:
                                break;
                        }
                    }
                })
                .catch(error => console.log(error))
                // Set loading prop to false when done.
                .finally(() => this.loading = false);
        },
        setSwiper() {
            if (this.$refs.monthButtonsSwiper) {
                const monthButtonSwiper = new Swiper(this.$refs.monthButtonsSwiper, {
                    modules: [Navigation],
                    slidesPerView: 'auto',
                    spaceBetween: 16,
                    navigation: {
                        nextEl: this.$refs.monthButtonsSwiperNext,
                        prevEl: this.$refs.monthButtonsSwiperPrev
                    },
                    on: {
                        init: function (swiper) {
                            // Force the final snap coordinate to equal the last slide coordinate
                            swiper.snapGrid[swiper.snapGrid.length - 1] = swiper.slidesGrid[swiper.slidesGrid.length - 1];
                        }
                    }
                });
            }
        },
        // Lazy loading data chunks handler.
        loadMore() {
            this.page++;
            this.fetchData();
        },
        // Data alteration function for reloading data with set filters.
        selectOption(facetAlias, event) {
            let optionAlias = event.target.value;
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            changedFacet.values = [optionAlias];
            this.fetchData(true);
        },
        selectCustomDate(from, to, label, clearCalendar = false) {
            if (clearCalendar && this.calendar) this.calendar.setDate({ clear: true });
            this.searchFrom = from;
            this.searchTo = to;
            this.searchDateLabel = label;
            this.fetchData(true);
        },
        handleBlur() {
            setTimeout(() => {
                this.isInputFocused = false;
            }, 200);
        },
        navigateDropdown(direction) {
            if (!this.data.suggestedSearchTerms.length) return;
            this.isInputFocused = true;

            let newIndex = this.selectedIndex + direction;

            if (newIndex < 0) newIndex = this.data.suggestedSearchTerms.length - 1;
            if (newIndex >= this.data.suggestedSearchTerms.length) newIndex = 0;

            this.selectedIndex = newIndex;
        },
        selectHighlighted() {
            if (this.selectedIndex >= 0 && this.selectedIndex < this.data.suggestedSearchTerms.length) {
                this.clickSuggestion(this.data.suggestedSearchTerms[this.selectedIndex].text);
            }
        },
        clickSuggestion(text) {
            this.searchText = text;
            this.changeSearchText();
            this.isInputFocused = false;
            this.selectedIndex = -1;
        },
        // Data alteration function for resetting all active filters and reloading data.
        reset() {
            this.data.facets.forEach(facet => facet.values = []);
            this.searchText = '';
            if (this.calendar) this.calendar.setDate({ clear: true });
            this.selectCustomDate('', '', '');
        },
        // Function for updating browser URL and state history with changed filters, search input or page.
        updateUrl() {
            if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                history.pushState({}, null, window.location.pathname);
                return;
            }
            const urlParams = new URLSearchParams();
            if (this.queryModel.Text) urlParams.append('text', this.queryModel.Text);
            if (this.searchDateLabel) urlParams.append('date', this.searchDateLabel);

            if (this.queryModel.FilterItems.length) {
                this.queryModel.FilterItems
                    .filter(filterItem => filterItem.Operator === 'In')
                    .forEach(filterItem => {
                        let facet = this.data.facets.find(facet => facet.alias === filterItem.Key);
                        urlParams.append(
                            facet.title.toLowerCase(),
                            facet.options.filter(option => filterItem.Values.includes(option.valueAlias)).map(option => option.text.toLowerCase()).join());
                    })
            }

            if (this.page > 1) urlParams.append('page', this.page);
            history.pushState({}, null, window.location.pathname + '?' + urlParams.toString());
        },
        redirect(url) {
            if (url) window.location.href = url;
        },

        // Check if a new month should be shown in the template and return the display value if so.
        showMonth(index, date) {
            let returnVal = null;
            if (index === 0) {
                returnVal = new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
            }
            else {
                let prevEvent = this.data.items[index - 1];
                if (prevEvent) {
                    if (this.toDateObj(date).getMonth() !== this.toDateObj(prevEvent.startDate).getMonth() || this.toDateObj(date).getYear() !== this.toDateObj(prevEvent.startDate).getYear()) {
                        returnVal = new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
                    }
                }
            }
            return returnVal;
        },
        // Check if a new year should be shown in the template and return the display value if so.
        showYear(index, date) {
            return index === 0 || this.toDateObj(date).getMonth() === 0 ? this.toDateObj(date).getFullYear() : null;
        },

        // Simple whitespace strip function.
        strip(value) {
            if (!value) return '';
            value = value.toString();
            return value.replace(/\s/g, '');
        }
    },
    async created() {
        if (await getCookie('listBlockView') !== '') this.blockView = false;
        // Data alteration function for reloading data with textual search input.
        this.changeSearchText = debounce(event => {
            this.fetchData(true);
        });
    },
    mounted() {
        // Set data props according to data attributes in HTML template.
        if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api;
        if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
        if (this.$el.dataset.sortkey) this.dataSortKey = this.$el.dataset.sortkey;
        if (this.$el.dataset.sortdirection) this.dataSortDirection = this.$el.dataset.sortdirection;
        if (this.$el.dataset.showxamountoftotalitems) this.dataAmountsSentence = this.$el.dataset.showxamountoftotalitems;
        if (this.$el.dataset.todaylabel) this.dataTodayLabel = this.$el.dataset.todaylabel;
        if (this.$el.dataset.weekendlabel) this.dataWeekendLabel = this.$el.dataset.weekendlabel;
        if (this.$el.dataset.weeklabel) this.dataWeekLabel = this.$el.dataset.weeklabel;

        // Construct query model and alter data props based on URL search params.
        const urlParams = new URLSearchParams(window.location.search);

        let pageParam = urlParams.has('page') ? parseInt(urlParams.get('page')) : 0;
        if (pageParam) urlParams.delete('page');
        if (pageParam < 2) pageParam = 0;

        this.searchText = urlParams.has('text') ? urlParams.get('text') : '';
        if (this.searchText) urlParams.delete('text');

        let filterParams = urlParams.toString() ? urlParams : null;

        // Initial data fetch.
        this.fetchData(true, filterParams, pageParam);
    },
    updated() {
        const calendarElement = Array.isArray(this.$refs.calendar) ? this.$refs.calendar[0] : this.$refs.calendar;

        if (calendarElement && !calendarElement.classList.contains('is-hidden') && !this.calendar) {
            let yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);

            const datePickerOptions = {
                autohide: true,
                language: 'nl',
                nextArrow: '<i class="las la-angle-right"></i>',
                prevArrow: '<i class="las la-angle-left"></i>',
                orientation: 'bottom',
                format: 'dd/mm/yyyy',
                minDate: yesterday,
            }

            this.calendar = calendarElement.datepicker || new Datepicker(calendarElement, datePickerOptions);
            this.calendarChangeHandler = (event) => {
                const date = event.detail.date;
                if (!date) return;

                this.selectCustomDate(
                    new Date(new Date(date).setHours(0, 0, 0, 0)).toISOString(),
                    new Date(new Date(date).setHours(23, 59, 59, 999)).toISOString(),
                    new Date(date).toLocaleDateString('nl-NL')
                );
            };
            calendarElement.addEventListener('changeDate', this.calendarChangeHandler);

            if (this.searchFrom && this.searchTo && this.searchFrom === new Date(new Date(this.searchTo).setHours(0, 0, 0, 0)).toISOString()) {
                this.calendar.setDate(new Date(this.searchFrom));
            }

            this.setSwiper();
        }
    },
};

export default agendaApp;
